import { useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch } from '../lib/api.js';
import { C, alpha } from '../lib/theme.js';
import { Button } from '../ui/ui.js';
import { Icon } from '../ui/icons.js';
import { hashField, parseCsv, type PiiField } from '../lib/hashPii.js';
import { codeToName, normalizeCountry } from '../lib/countries.js';
import type { AudienceBucket } from '../lib/types.js';

// A lookalike is built from your WHOLE guest list (the seed), so the target
// country doesn't need many guests of its own — only the seed needs ~100 matched.
// We use this only to PRE-SELECT the markets where you have real representation;
// every market stays tickable.
const PRESELECT_MIN = 100;

/**
 * Lookalike builder — upload a guest CSV, hash it in the browser, and create a
 * Meta Custom Audience + per-country Lookalikes that land as reusable buckets.
 * Raw PII never leaves the device: only SHA-256 hashes are sent.
 */

// The target markets Phuket Cleanse actually runs in (RULES §3).
const MARKETS: { code: string; name: string }[] = [
  { code: 'AE', name: 'UAE' },
  { code: 'AU', name: 'Australia' },
  { code: 'HK', name: 'Hong Kong' },
  { code: 'SG', name: 'Singapore' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'IE', name: 'Ireland' },
  { code: 'US', name: 'United States' },
];

// The columns we can map, in the order they go into Meta's schema.
const FIELDS: { key: PiiField; label: string; guess: RegExp }[] = [
  { key: 'EMAIL', label: 'Email', guess: /e-?mail/i },
  { key: 'PHONE', label: 'Phone', guess: /phone|mobile|cell|tel/i },
  { key: 'FN', label: 'First name', guess: /first|given|^name$|full.?name/i },
  { key: 'LN', label: 'Last name', guess: /last|surname|family/i },
  { key: 'GEN', label: 'Gender', guess: /gender|sex/i },
];

type Mapping = Partial<Record<PiiField, number>>; // field → column index

export function LookalikeUploader({ orgId, onDone }: { orgId: string; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [header, setHeader] = useState<string[]>([]);
  const [rows, setRows] = useState<string[][]>([]);
  const [mapping, setMapping] = useState<Mapping>({});
  const [countryCol, setCountryCol] = useState<number | undefined>(undefined);
  const [name, setName] = useState('Past guests');
  const [countries, setCountries] = useState<string[]>(['AU']);
  const [ratio, setRatio] = useState(0.01);
  const [progress, setProgress] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  function onFile(file: File) {
    setError(null);
    setResult(null);
    const reader = new FileReader();
    reader.onload = () => {
      const parsed = parseCsv(String(reader.result ?? ''));
      if (parsed.length < 2) {
        setError('That file has no data rows.');
        return;
      }
      const head = parsed[0]!;
      setHeader(head);
      setRows(parsed.slice(1));
      // Auto-guess the mapping from header names.
      const guess: Mapping = {};
      for (const f of FIELDS) {
        const idx = head.findIndex((h) => f.guess.test(h));
        if (idx >= 0) guess[f.key] = idx;
      }
      setMapping(guess);
      const cIdx = head.findIndex((h) => /country|nation|region/i.test(h));
      setCountryCol(cIdx >= 0 ? cIdx : undefined);
    };
    reader.readAsText(file);
  }

  // Guest distribution by country (read in-browser from the mapped column).
  // Raw rows never leave the device — this is just counts for smarter market picks.
  const dist = useMemo(() => {
    if (countryCol === undefined || rows.length === 0) {
      return { rows: [] as { code: string; name: string; count: number }[], unknown: 0, total: 0 };
    }
    const counts = new Map<string, { name: string; count: number }>();
    let unknown = 0;
    for (const r of rows) {
      const hit = normalizeCountry(r[countryCol] ?? '');
      if (!hit) {
        unknown++;
        continue;
      }
      const cur = counts.get(hit.code);
      if (cur) cur.count++;
      else counts.set(hit.code, { name: hit.name, count: 1 });
    }
    const list = [...counts.entries()]
      .map(([code, v]) => ({ code, name: v.name, count: v.count }))
      .sort((a, b) => b.count - a.count);
    return { rows: list, unknown, total: rows.length };
  }, [rows, countryCol]);

  // When a distribution is available, default-select the markets with enough
  // guests to seed a lookalike (or the top 2 if none clear the bar).
  useEffect(() => {
    if (dist.rows.length === 0) return;
    const strong = dist.rows.filter((d) => d.count >= PRESELECT_MIN).map((d) => d.code);
    setCountries(strong.length > 0 ? strong : dist.rows.slice(0, 2).map((d) => d.code));
  }, [dist]);

  const mappedFields = FIELDS.filter((f) => mapping[f.key] !== undefined).map((f) => f.key);
  const canBuild =
    rows.length > 0 &&
    (mapping.EMAIL !== undefined || mapping.PHONE !== undefined) &&
    countries.length > 0 &&
    !!name.trim() &&
    !busy;

  async function build() {
    if (!canBuild) return;
    setBusy(true);
    setError(null);
    setResult(null);
    setProgress(0);
    try {
      const schema = mappedFields;
      const data: string[][] = [];
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i]!;
        const hashed = await Promise.all(schema.map((f) => hashField(f, row[mapping[f]!] ?? '')));
        data.push(hashed);
        if (i % 200 === 0) setProgress(Math.round((i / rows.length) * 100));
      }
      setProgress(100);
      const picked = countries.map((code) => ({ code, name: codeToName(code) }));
      const d = await apiFetch<{
        received: number;
        invalid: number;
        buckets: AudienceBucket[];
        skipped?: { country: string; reason: string }[];
      }>(`/api/orgs/${orgId}/audiences/lookalike`, {
        method: 'POST',
        body: JSON.stringify({ name: name.trim(), schema, data, countries: picked, ratio }),
      });
      const skips = d.skipped ?? [];
      setResult(
        `Uploaded ${d.received.toLocaleString()} matched rows${
          d.invalid ? ` (${d.invalid} skipped)` : ''
        }. Created ${d.buckets.length} lookalike audience${
          d.buckets.length === 1 ? '' : 's'
        }. They take a few hours to populate in Meta, then appear below and in the Launch picker.${
          skips.length
            ? ` Skipped ${skips.map((s) => `${s.country} (${s.reason})`).join(', ')}.`
            : ''
        }`,
      );
      setHeader([]);
      setRows([]);
      setMapping({});
      setCountryCol(undefined);
      if (fileRef.current) fileRef.current.value = '';
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setBusy(false);
      setProgress(null);
    }
  }

  if (!open) {
    return (
      <Button
        onClick={() => setOpen(true)}
        style={{ marginBottom: 14, display: 'inline-flex', alignItems: 'center', gap: 7 }}
      >
        <Icon name="users" size={15} />
        Build a lookalike from your guest list
      </Button>
    );
  }

  return (
    <div
      style={{
        border: `1px solid ${C.line}`,
        borderRadius: 12,
        padding: 16,
        marginBottom: 16,
        background: C.surface,
      }}
    >
      <div style={{ fontSize: 13.5, fontWeight: 600, color: C.primary, marginBottom: 4 }}>
        Build a lookalike from your guest list
      </div>
      <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5, marginBottom: 12 }}>
        Upload a Cloudbeds guest CSV (email + phone are the key columns). It&apos;s hashed here in
        your browser, so the raw list never leaves your device. Meta finds new people who resemble
        your real guests.
      </div>

      {error && <div style={{ color: C.act, fontSize: 12.5, marginBottom: 10 }}>{error}</div>}
      {result && (
        <div
          style={{
            fontSize: 12.5,
            color: C.good,
            background: alpha(C.good, 10),
            border: `1px solid ${alpha(C.good, 25)}`,
            borderRadius: 8,
            padding: '10px 12px',
            marginBottom: 12,
            lineHeight: 1.5,
          }}
        >
          {result}
        </div>
      )}

      {rows.length === 0 ? (
        <input
          ref={fileRef}
          type="file"
          accept=".csv,text/csv"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onFile(f);
          }}
          style={{ fontSize: 12.5, color: C.secondary }}
        />
      ) : (
        <>
          <div style={{ fontSize: 12, color: C.secondary, marginBottom: 10 }}>
            {rows.length.toLocaleString()} rows loaded. Map your columns:
          </div>
          <div style={{ display: 'grid', gap: 8, marginBottom: 14 }}>
            {FIELDS.map((f) => (
              <div key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ width: 90, fontSize: 12.5, color: C.muted }}>
                  {f.label}
                  {f.key === 'EMAIL' ? '' : ' (optional)'}
                </span>
                <select
                  value={mapping[f.key] ?? -1}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    setMapping((m) => {
                      const next = { ...m };
                      if (v < 0) delete next[f.key];
                      else next[f.key] = v;
                      return next;
                    });
                  }}
                  style={select}
                >
                  <option value={-1}>— not mapped —</option>
                  {header.map((h, i) => (
                    <option key={i} value={i}>
                      {h || `Column ${i + 1}`}
                    </option>
                  ))}
                </select>
              </div>
            ))}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ width: 90, fontSize: 12.5, color: C.muted }}>Country</span>
              <select
                value={countryCol ?? -1}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  setCountryCol(v < 0 ? undefined : v);
                }}
                style={select}
              >
                <option value={-1}>— not mapped —</option>
                {header.map((h, i) => (
                  <option key={i} value={i}>
                    {h || `Column ${i + 1}`}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {mapping.EMAIL === undefined && mapping.PHONE === undefined && (
            <div style={{ fontSize: 11.5, color: C.watch, marginBottom: 12 }}>
              Map at least an email or phone column.
            </div>
          )}

          <div className="label-mono" style={{ marginBottom: 6 }}>
            Audience name
          </div>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={{ ...select, width: '100%', marginBottom: 14 }}
          />

          {dist.rows.length > 0 && (
            <div
              style={{
                border: `1px solid ${C.line}`,
                borderRadius: 10,
                padding: '12px 14px',
                marginBottom: 14,
                background: C.surface2,
              }}
            >
              <div className="label-mono" style={{ marginBottom: 10 }}>
                Your guests by country
              </div>
              {dist.rows.slice(0, 10).map((d) => {
                const pct = dist.total ? (d.count / dist.total) * 100 : 0;
                return (
                  <div
                    key={d.code}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 7 }}
                  >
                    <span style={{ width: 120, fontSize: 12, color: C.secondary }}>{d.name}</span>
                    <div
                      style={{
                        flex: 1,
                        height: 7,
                        borderRadius: 4,
                        background: alpha(C.basil, 12),
                        overflow: 'hidden',
                      }}
                    >
                      <div style={{ width: `${pct}%`, height: '100%', background: C.basil }} />
                    </div>
                    <span
                      style={{
                        width: 88,
                        textAlign: 'right',
                        font: `500 11px 'IBM Plex Mono',monospace`,
                        color: C.muted,
                      }}
                    >
                      {d.count.toLocaleString()} · {pct.toFixed(0)}%
                    </span>
                  </div>
                );
              })}
              {dist.unknown > 0 && (
                <div style={{ fontSize: 11, color: C.faint, marginTop: 6 }}>
                  {dist.unknown.toLocaleString()} rows had an unrecognized or blank country.
                </div>
              )}
            </div>
          )}

          <div className="label-mono" style={{ marginBottom: 6 }}>
            Build lookalikes in these markets
          </div>
          {dist.rows.length > 0 && (
            <div style={{ fontSize: 11.5, color: C.faint, marginBottom: 8 }}>
              Pre-selected where you have the most guests. You can target any market — a lookalike
              is built from your whole guest list, so the target country doesn&apos;t need many
              guests of its own.
            </div>
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
            {(dist.rows.length > 0
              ? dist.rows.map((d) => ({ code: d.code, name: d.name, count: d.count }))
              : MARKETS.map((m) => ({ ...m, count: undefined as number | undefined }))
            ).map((m) => {
              const on = countries.includes(m.code);
              // Low-representation markets are dimmed as a soft signal, but stay
              // fully selectable — the lookalike uses the whole seed, so any
              // target country works (only the seed needs to be big enough).
              const thin = m.count !== undefined && m.count < PRESELECT_MIN;
              return (
                <button
                  key={m.code}
                  type="button"
                  title={
                    thin
                      ? `Only ${m.count} guests here — still fine; it uses your whole list`
                      : undefined
                  }
                  onClick={() =>
                    setCountries((cs) => (on ? cs.filter((x) => x !== m.code) : [...cs, m.code]))
                  }
                  style={{
                    padding: '6px 12px',
                    borderRadius: 8,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    fontSize: 12,
                    fontWeight: 600,
                    border: `1px solid ${on ? C.basil : C.line}`,
                    background: on ? alpha(C.basil, 12) : C.surface,
                    color: on ? C.basil : thin ? C.muted : C.secondary,
                    opacity: thin && !on ? 0.75 : 1,
                  }}
                >
                  {on ? '✓ ' : ''}
                  {m.name}
                  {m.count !== undefined && (
                    <span style={{ color: on ? C.basil : C.faint, fontWeight: 500 }}>
                      {' '}
                      {m.count.toLocaleString()}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="label-mono" style={{ marginBottom: 6 }}>
            Similarity
          </div>
          <select
            value={ratio}
            onChange={(e) => setRatio(Number(e.target.value))}
            style={{ ...select, marginBottom: 14 }}
          >
            <option value={0.01}>1% — tightest match (recommended)</option>
            <option value={0.03}>3% — broader reach</option>
            <option value={0.05}>5% — widest</option>
          </select>

          {progress !== null && (
            <div style={{ fontSize: 12, color: C.basil, marginBottom: 10 }}>
              {progress < 100 ? `Hashing your list… ${progress}%` : 'Uploading to Meta…'}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10 }}>
            <Button
              variant="primary"
              onClick={() => void build()}
              style={{ opacity: canBuild ? 1 : 0.55 }}
            >
              {busy ? 'Working…' : 'Hash & build lookalikes'}
            </Button>
            <Button
              onClick={() => {
                setHeader([]);
                setRows([]);
                setMapping({});
                setCountryCol(undefined);
                if (fileRef.current) fileRef.current.value = '';
              }}
            >
              Choose a different file
            </Button>
          </div>
        </>
      )}

      <div style={{ marginTop: 14 }}>
        <span
          onClick={() => setOpen(false)}
          style={{ fontSize: 11.5, color: C.muted, cursor: 'pointer' }}
        >
          Close
        </span>
      </div>
    </div>
  );
}

const select: React.CSSProperties = {
  boxSizing: 'border-box',
  background: C.surface2,
  border: `1px solid ${C.line}`,
  borderRadius: 8,
  padding: '8px 11px',
  fontSize: 13,
  color: C.primary,
  fontFamily: 'inherit',
};
